/**
 * Diagnostics Export Engine - Phase 14
 * 
 * Exports sanitized diagnostics bundles in various formats.
 */

import { getDiagnosticsBundle } from './diagnosticsStore';
import { sanitizeDiagnosticsBundle } from './sanitizer';

// ==================== EXPORT FUNCTIONS ====================

/**
 * Export diagnostics bundle as JSON
 * @param {Object} scope
 * @param {Object} user
 * @returns {Promise<void>}
 */
export async function exportDiagnosticsBundleJson(scope = {}, user = {}) {
  const bundle = await getDiagnosticsBundle(scope, user);
  const filename = buildDiagnosticsFilename(scope, 'json');
  
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  downloadBlob(blob, filename);
  
  // Record export event
  recordDiagnosticsExport('json', scope);
}

/**
 * Export diagnostics summary as CSV
 * @param {Object} scope
 * @param {Object} user
 * @returns {Promise<void>}
 */
export async function exportDiagnosticsSummaryCsv(scope = {}, user = {}) {
  const bundle = await getDiagnosticsBundle(scope, user);
  const rows = buildDiagnosticsSummaryRows(bundle);
  const csv = convertRowsToCsv(rows);
  const filename = buildDiagnosticsFilename(scope, 'csv');
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
  
  // Record export event
  recordDiagnosticsExport('csv', scope);
}

/**
 * Export diagnostics as ZIP (JSON + CSV)
 * @param {Object} scope
 * @param {Object} user
 * @returns {Promise<void>}
 */
export async function exportDiagnosticsZip(scope = {}, user = {}) {
  // Note: ZIP export requires a library like JSZip
  // For now, export both files separately
  console.warn('[ExportEngine] ZIP export not implemented, exporting JSON and CSV separately');
  
  await exportDiagnosticsBundleJson(scope, user);
  await exportDiagnosticsSummaryCsv(scope, user);
}

/**
 * Build filename for diagnostics export
 * @param {Object} scope
 * @param {string} format - json | csv | zip
 * @returns {string}
 */
export function buildDiagnosticsFilename(scope, format) {
  const parts = ['asterpos-diagnostics'];
  
  if (scope.merchantId) {
    parts.push(`m${scope.merchantId}`);
  }
  if (scope.locationId) {
    parts.push(`l${scope.locationId}`);
  }
  if (scope.terminalId) {
    parts.push(`t${scope.terminalId}`);
  }
  
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  parts.push(timestamp);
  
  return `${parts.join('_')}.${format}`;
}

/**
 * Build CSV summary rows from bundle
 * @param {Object} bundle
 * @returns {Array<Object>}
 */
export function buildDiagnosticsSummaryRows(bundle) {
  const rows = [];
  
  // Header row with metadata
  rows.push({
    Category: 'Metadata',
    Metric: 'Generated At',
    Value: new Date(bundle.generatedAt).toISOString(),
    Notes: ''
  });
  
  rows.push({
    Category: 'Metadata',
    Metric: 'Scope',
    Value: formatScope(bundle.scope),
    Notes: ''
  });
  
  // App Context
  rows.push({
    Category: 'App Context',
    Metric: 'App Version',
    Value: bundle.appContext?.appVersion || 'N/A',
    Notes: ''
  });
  
  rows.push({
    Category: 'App Context',
    Metric: 'Environment',
    Value: bundle.appContext?.environment || 'N/A',
    Notes: ''
  });
  
  rows.push({
    Category: 'App Context',
    Metric: 'Browser',
    Value: bundle.appContext?.browser || 'N/A',
    Notes: ''
  });
  
  rows.push({
    Category: 'App Context',
    Metric: 'Device Type',
    Value: bundle.appContext?.deviceType || 'N/A',
    Notes: ''
  });
  
  // Queue Snapshot
  rows.push({
    Category: 'Queue Snapshot',
    Metric: 'Pending Count',
    Value: bundle.queueSnapshot?.pendingCount || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'Queue Snapshot',
    Metric: 'Failed Count',
    Value: bundle.queueSnapshot?.failedCount || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'Queue Snapshot',
    Metric: 'Conflict Count',
    Value: bundle.queueSnapshot?.conflictCount || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'Queue Snapshot',
    Metric: 'Blocked Count',
    Value: bundle.queueSnapshot?.blockedCount || 0,
    Notes: ''
  });
  
  if (bundle.queueSnapshot?.oldestPendingAgeMs) {
    rows.push({
      Category: 'Queue Snapshot',
      Metric: 'Oldest Pending Age',
      Value: formatDuration(bundle.queueSnapshot.oldestPendingAgeMs),
      Notes: ''
    });
  }
  
  // Replay Summary
  rows.push({
    Category: 'Replay Summary',
    Metric: 'Success Count',
    Value: bundle.replaySummary?.successCount || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'Replay Summary',
    Metric: 'Failure Count',
    Value: bundle.replaySummary?.failureCount || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'Replay Summary',
    Metric: 'Conflict Count',
    Value: bundle.replaySummary?.conflictCount || 0,
    Notes: ''
  });
  
  const totalReplays = (bundle.replaySummary?.successCount || 0) + 
                       (bundle.replaySummary?.failureCount || 0) + 
                       (bundle.replaySummary?.conflictCount || 0);
  if (totalReplays > 0) {
    const successRate = ((bundle.replaySummary?.successCount || 0) / totalReplays * 100).toFixed(1);
    rows.push({
      Category: 'Replay Summary',
      Metric: 'Success Rate',
      Value: `${successRate}%`,
      Notes: ''
    });
  }
  
  // Incident Summary
  rows.push({
    Category: 'Incidents',
    Metric: 'Open Count',
    Value: bundle.incidentSummary?.openCount || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'Incidents',
    Metric: 'Critical Count',
    Value: bundle.incidentSummary?.criticalCount || 0,
    Notes: bundle.incidentSummary?.criticalCount > 0 ? 'ATTENTION REQUIRED' : ''
  });
  
  rows.push({
    Category: 'Incidents',
    Metric: 'Blocked Payments',
    Value: bundle.incidentSummary?.blockedPaymentCount || 0,
    Notes: bundle.incidentSummary?.blockedPaymentCount > 0 ? 'REVENUE IMPACT' : ''
  });
  
  rows.push({
    Category: 'Incidents',
    Metric: 'Escalated Count',
    Value: bundle.incidentSummary?.escalatedCount || 0,
    Notes: ''
  });
  
  // POS Recovery
  rows.push({
    Category: 'POS Recovery',
    Metric: 'Unsynced Orders',
    Value: bundle.posRecoverySummary?.unsyncedOrders || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Recovery',
    Metric: 'Blocked Payments',
    Value: bundle.posRecoverySummary?.blockedPayments || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Recovery',
    Metric: 'Failed Kitchen',
    Value: bundle.posRecoverySummary?.failedKitchen || 0,
    Notes: ''
  });
  
  rows.push({
    Category: 'POS Recovery',
    Metric: 'Stale Items',
    Value: bundle.posRecoverySummary?.staleItems || 0,
    Notes: bundle.posRecoverySummary?.staleItems > 0 ? 'Items older than 1 hour' : ''
  });
  
  // Network
  rows.push({
    Category: 'Network',
    Metric: 'Current Status',
    Value: bundle.networkDiagnostics?.currentStatus || 'Unknown',
    Notes: ''
  });
  
  rows.push({
    Category: 'Network',
    Metric: 'Backend Reachable',
    Value: bundle.networkDiagnostics?.backendReachable === true ? 'Yes' : 
           bundle.networkDiagnostics?.backendReachable === false ? 'No' : 'Unknown',
    Notes: ''
  });
  
  if (bundle.networkDiagnostics?.effectiveConnection) {
    rows.push({
      Category: 'Network',
      Metric: 'Connection Type',
      Value: bundle.networkDiagnostics.effectiveConnection.type || 'Unknown',
      Notes: ''
    });
  }
  
  return rows;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Download blob as file
 * @param {Blob} blob
 * @param {string} filename
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convert rows to CSV string
 * @param {Array<Object>} rows
 * @returns {string}
 */
function convertRowsToCsv(rows) {
  if (rows.length === 0) return '';
  
  const headers = Object.keys(rows[0]);
  const csvRows = [headers.join(',')];
  
  for (const row of rows) {
    const values = headers.map(header => {
      const val = row[header];
      // Escape quotes and wrap in quotes if contains comma
      if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    });
    csvRows.push(values.join(','));
  }
  
  return csvRows.join('\n');
}

/**
 * Format scope for display
 * @param {Object} scope
 * @returns {string}
 */
function formatScope(scope) {
  const parts = [];
  if (scope.merchantId) parts.push(`Merchant: ${scope.merchantId}`);
  if (scope.locationId) parts.push(`Location: ${scope.locationId}`);
  if (scope.terminalId) parts.push(`Terminal: ${scope.terminalId}`);
  if (scope.dateFrom) parts.push(`From: ${new Date(scope.dateFrom).toLocaleDateString()}`);
  if (scope.dateTo) parts.push(`To: ${new Date(scope.dateTo).toLocaleDateString()}`);
  return parts.length > 0 ? parts.join(', ') : 'All';
}

/**
 * Format duration in human readable format
 * @param {number} ms
 * @returns {string}
 */
function formatDuration(ms) {
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  if (ms < 86400000) return `${Math.round(ms / 3600000)}h`;
  return `${Math.round(ms / 86400000)}d`;
}

/**
 * Record diagnostics export event
 * @param {string} format
 * @param {Object} scope
 */
function recordDiagnosticsExport(format, scope) {
  console.log('[DiagnosticsExport]', { format, scope, timestamp: Date.now() });
  
  // Could integrate with metrics system here
  if (typeof window !== 'undefined' && window.__asterpos_metrics) {
    window.__asterpos_metrics.recordDiagnosticsExport?.({ format, scope });
  }
}

export default {
  exportDiagnosticsBundleJson,
  exportDiagnosticsSummaryCsv,
  exportDiagnosticsZip,
  buildDiagnosticsFilename,
  buildDiagnosticsSummaryRows
};
